import React, { useEffect, useState } from 'react';
import { Button, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { StaffDailyHours, formatDailyHours, parseDailyHours } from '../utils/staffDailyHours';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    color: theme.palette.common.white,
  },
  input: {
    '& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root': {
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[500] },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.common.white },
    '& .Mui-error': { color: theme.palette.error.main },
    '& input': { colorScheme: 'dark' },
  },
  applied: { flexBasis: '100%' },
}));

interface Props {
  value: StaffDailyHours | null;
  onApply: (value: StaffDailyHours | null) => void;
  hospitalTimezone: string;
}

const StaffDailyHoursFilter: React.FC<Props> = ({ value, onApply, hospitalTimezone }) => {
  const classes = useStyles();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [invalidStart, setInvalidStart] = useState(false);
  const [invalidEnd, setInvalidEnd] = useState(false);
  const [inputResetKey, setInputResetKey] = useState(0);
  useEffect(() => {
    const [from, to] = value ? formatDailyHours(value).split('–') : ['', ''];
    setInvalidStart(false);
    setInvalidEnd(false);
    setStart(from);
    setEnd(to === '24:00' ? '' : to);
  }, [value]);
  let draft: StaffDailyHours | null = null;
  let error = '';
  try {
    if (invalidStart || invalidEnd) throw new Error('Complete the time or clear the entire field.');
    draft = parseDailyHours(start, end);
  } catch (e) {
    error = (e as Error).message;
  }
  const dirty = draft?.dailyStartMinute !== value?.dailyStartMinute || draft?.dailyEndMinute !== value?.dailyEndMinute;

  return (
    <form
      className={classes.root}
      aria-label="Daily hours filter"
      onSubmit={event => {
        event.preventDefault();
        if (!error) onApply(draft);
      }}
    >
      <Typography variant="body2">Daily hours</Typography>
      <TextField
        className={classes.input}
        key={`from-${inputResetKey}`}
        label="From"
        type="time"
        variant="outlined"
        size="small"
        value={start}
        onChange={event => setStart(event.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          step: 60,
          'aria-label': 'Daily start time',
          onInput: (event: React.FormEvent<HTMLInputElement>) => {
            setInvalidStart(event.currentTarget.validity.badInput);
            setStart(event.currentTarget.value);
          },
          onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) =>
            setInvalidStart(event.currentTarget.validity.badInput),
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => setInvalidStart(event.currentTarget.validity.badInput),
        }}
        helperText="Blank = start of day"
        error={!!error}
      />
      <TextField
        className={classes.input}
        key={`to-${inputResetKey}`}
        label="To"
        type="time"
        variant="outlined"
        size="small"
        value={end}
        onChange={event => setEnd(event.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          step: 60,
          'aria-label': 'Daily end time',
          onInput: (event: React.FormEvent<HTMLInputElement>) => {
            setInvalidEnd(event.currentTarget.validity.badInput);
            setEnd(event.currentTarget.value);
          },
          onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) =>
            setInvalidEnd(event.currentTarget.validity.badInput),
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => setInvalidEnd(event.currentTarget.validity.badInput),
        }}
        helperText="Blank = end of day"
        error={!!error}
      />
      <Button type="submit" variant="contained" color="primary" disabled={!!error || !dirty}>
        Apply
      </Button>
      <Button
        type="button"
        color="inherit"
        disabled={!start && !end && !invalidStart && !invalidEnd}
        onClick={() => {
          setInputResetKey(key => key + 1);
          setInvalidStart(false);
          setInvalidEnd(false);
          setStart('');
          setEnd('');
        }}
      >
        All day
      </Button>
      <Typography variant="body2" className={classes.applied} aria-live="polite">
        Applied: {formatDailyHours(value)} · Each selected day · {hospitalTimezone}
        {!error && dirty ? ' · Changes pending — click Apply' : ''}
      </Typography>
      {error && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
    </form>
  );
};

export default StaffDailyHoursFilter;
